import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Plus, Minus, ShoppingCart, X, ChevronRight, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

export default function CustomerMenu() {
  const { restaurantId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCart, setShowCart] = useState(false);
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [specialNote, setSpecialNote] = useState('');
  const catRefs = useRef({});

  useEffect(() => {
    api.get(`/api/menu/${restaurantId}`)
      .then(res => { setData(res.data); })
      .catch(err => setError(err.response?.data?.detail || 'Menu not found'))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const isOpen = () => {
    if (!data?.restaurant?.opening_time || !data?.restaurant?.closing_time) return true;
    const now = new Date();
    const [oh, om] = data.restaurant.opening_time.split(':').map(Number);
    const [ch, cm] = data.restaurant.closing_time.split(':').map(Number);
    const cur = now.getHours() * 60 + now.getMinutes();
    const open = oh * 60 + om;
    const close = ch * 60 + cm;
    return cur >= open && cur <= close;
  };

  const addToCart = (item) => {
    setCart(prev => ({ ...prev, [item.id]: (prev[item.id] || 0) + 1 }));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const next = { ...prev };
      if (next[itemId] <= 1) delete next[itemId];
      else next[itemId]--;
      return next;
    });
  };

  const cartItems = data ? Object.entries(cart).map(([id, qty]) => ({
    ...data.items.find(i => i.id === id),
    qty
  })).filter(i => i.name) : [];

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const subtotal = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);

  const getFilteredItems = (catId) => {
    let items = data?.items || [];
    if (search) items = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
    if (catId && catId !== 'all') items = items.filter(i => i.category_id === catId);
    return items;
  };

  const handleOrder = () => {
    if (!tableNumber.trim()) { toast.error('Please enter your table number'); return; }
    if (cartItems.length === 0) { toast.error('Your cart is empty'); return; }

    const lines = cartItems.map(i => `• ${i.qty}x ${i.name}`).join('\n');
    const message = [
      `🍽️ *New Order*`,
      `─────────────────`,
      `📍 Table: ${tableNumber}`,
      customerName ? `👤 Name: ${customerName}` : '',
      ``,
      `*Items:*`,
      lines,
      ``,
      `💰 Subtotal: ₹${subtotal}`,
      `_(Taxes & service charge extra as applicable)_`,
      specialNote ? `\n📝 Note: ${specialNote}` : '',
    ].filter(l => l !== null).join('\n');

    const whatsapp = data.restaurant.whatsapp.replace(/[^0-9]/g, '');
    const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setShowCart(false);
  };

  const scrollToCategory = (catId) => {
    setActiveCategory(catId);
    if (catId === 'all') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = catRefs.current[catId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) return (
    <div className="page-loader">
      <div style={styles.loadingLogo}>🍽️</div>
      <div className="spinner" />
      <p style={{ color: '#7F8C8D', fontSize: 14 }}>Loading menu...</p>
    </div>
  );

  if (error) return (
    <div className="page-loader">
      <AlertCircle size={48} color="#E74C3C" />
      <h2 style={{ color: '#2C3E50' }}>Oops!</h2>
      <p style={{ color: '#7F8C8D' }}>{error}</p>
    </div>
  );

  const { restaurant, categories, items } = data;
  const open = isOpen();

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logoBox}>
            {restaurant.logo
              ? <img src={restaurant.logo} alt="" style={styles.logoImg} />
              : <span style={{ fontSize: 28 }}>🍽️</span>}
          </div>
          <div style={styles.headerInfo}>
            <h1 style={styles.restaurantName}>{restaurant.name}</h1>
            {restaurant.address && <p style={styles.restaurantAddr}>📍 {restaurant.address}</p>}
          </div>
          <span className={`badge ${open ? 'badge-success' : 'badge-danger'}`} style={{ flexShrink: 0 }}>
            {open ? '● Open' : '● Closed'}
          </span>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchWrapper}>
        <Search size={16} color="#7F8C8D" style={styles.searchIcon} />
        <input
          style={styles.searchInput}
          placeholder="Search dishes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')} style={styles.searchClear}><X size={14} /></button>
        )}
      </div>

      {/* Category Tabs */}
      <div style={styles.catTabs}>
        <button
          style={{ ...styles.catTab, ...(activeCategory === 'all' ? styles.catTabActive : {}) }}
          onClick={() => scrollToCategory('all')}
        >All</button>
        {categories.map(cat => (
          <button
            key={cat.id}
            style={{ ...styles.catTab, ...(activeCategory === cat.id ? styles.catTabActive : {}) }}
            onClick={() => scrollToCategory(cat.id)}
          >{cat.name}</button>
        ))}
      </div>

      {/* Menu Content */}
      <div style={styles.menuContent}>
        {search ? (
          // Search results
          <div>
            <p style={styles.searchResultLabel}>
              {getFilteredItems('all').length} result{getFilteredItems('all').length !== 1 ? 's' : ''} for "{search}"
            </p>
            {getFilteredItems('all').length === 0 ? (
              <div className="empty-state"><h3>No dishes found</h3><p>Try a different search</p></div>
            ) : (
              getFilteredItems('all').map(item => (
                <MenuItem key={item.id} item={item} cart={cart} onAdd={addToCart} onRemove={removeFromCart} />
              ))
            )}
          </div>
        ) : (
          // By category
          categories.map(cat => {
            const catItems = getFilteredItems(cat.id).filter(i => true); // all items for this cat
            const visibleItems = items.filter(i => i.category_id === cat.id);
            if (visibleItems.length === 0) return null;
            return (
              <div key={cat.id} ref={el => catRefs.current[cat.id] = el} style={styles.categorySection}>
                <h2 style={styles.categoryTitle}>{cat.name}</h2>
                {visibleItems.map(item => (
                  <MenuItem key={item.id} item={item} cart={cart} onAdd={addToCart} onRemove={removeFromCart} />
                ))}
              </div>
            );
          })
        )}
        <div style={{ height: cartCount > 0 ? 100 : 24 }} />
      </div>

      {/* Cart Button */}
      {cartCount > 0 && (
        <button style={styles.cartBtn} onClick={() => setShowCart(true)} className="slide-up">
          <div style={styles.cartBtnLeft}>
            <span style={styles.cartBtnCount}>{cartCount}</span>
            <span style={styles.cartBtnText}>View Cart</span>
          </div>
          <div style={styles.cartBtnRight}>
            <span>₹{subtotal}</span>
            <ChevronRight size={16} />
          </div>
        </button>
      )}

      {/* Cart Sheet */}
      {showCart && (
        <div style={styles.overlay} onClick={() => setShowCart(false)}>
          <div style={styles.cartSheet} onClick={e => e.stopPropagation()} className="slide-up">
            <div style={styles.cartHeader}>
              <h2 style={styles.cartTitle}>Your Order</h2>
              <button onClick={() => setShowCart(false)} style={styles.closeBtn}><X size={20} /></button>
            </div>

            {/* Cart Items */}
            <div style={styles.cartItems}>
              {cartItems.map(item => (
                <div key={item.id} style={styles.cartItem}>
                  <div style={styles.cartItemInfo}>
                    <span style={{ fontSize: 11 }}>{item.is_veg ? '🟢' : '🔴'}</span>
                    <span style={styles.cartItemName}>{item.name}</span>
                  </div>
                  <div style={styles.cartItemRight}>
                    <div style={styles.qtyControl}>
                      <button style={styles.qtyBtn} onClick={() => removeFromCart(item.id)}><Minus size={12} /></button>
                      <span style={styles.qtyNum}>{item.qty}</span>
                      <button style={styles.qtyBtn} onClick={() => addToCart(item)}><Plus size={12} /></button>
                    </div>
                    <span style={styles.cartItemPrice}>₹{item.price * item.qty}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Subtotal */}
            <div style={styles.subtotalRow}>
              <span style={styles.subtotalLabel}>Subtotal</span>
              <span style={styles.subtotalAmount}>₹{subtotal}</span>
            </div>

            {/* Tax Note */}
            <div style={styles.taxNote}>
              <AlertCircle size={14} color="#92400e" style={{ flexShrink: 0 }} />
              <p style={styles.taxNoteText}>
                This is an estimated amount only. Final bill will include applicable taxes and service charges as per restaurant policy.
              </p>
            </div>

            {/* Order Details */}
            <div style={styles.orderDetails}>
              <div className="form-group">
                <label className="label">Table Number <span style={{ color: '#E74C3C' }}>*</span></label>
                <input
                  className="input"
                  placeholder="Enter your table number"
                  value={tableNumber}
                  onChange={e => setTableNumber(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="label">Your Name <span style={{ color: '#7F8C8D', fontWeight: 400, fontSize: 11 }}>(optional)</span></label>
                <input
                  className="input"
                  placeholder="e.g. Rahul"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Special Instructions <span style={{ color: '#7F8C8D', fontWeight: 400, fontSize: 11 }}>(optional)</span></label>
                <textarea
                  className="input"
                  placeholder="e.g. Less spicy, no onion..."
                  rows={2}
                  value={specialNote}
                  onChange={e => setSpecialNote(e.target.value)}
                  style={{ resize: 'none' }}
                />
              </div>
            </div>

            {/* WhatsApp Order Button */}
            <button className="btn btn-whatsapp" onClick={handleOrder}>
              <span style={{ fontSize: 18 }}>💬</span>
              Order on WhatsApp
            </button>
            <p style={styles.waHint}>Opens WhatsApp with your order. Just hit Send!</p>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ item, cart, onAdd, onRemove }) {
  const qty = cart[item.id] || 0;

  return (
    <div style={{ ...styles.itemCard, opacity: item.is_available ? 1 : 0.55 }}>
      <div style={styles.itemContent}>
        <div style={styles.itemLeft}>
          <div style={styles.itemVegRow}>
            <span style={{ fontSize: 12 }}>{item.is_veg ? '🟢' : '🔴'}</span>
            <span style={styles.itemName}>{item.name}</span>
          </div>
          {item.description && <p style={styles.itemDesc}>{item.description}</p>}
          <span style={styles.itemPrice}>₹{item.price}</span>
          {!item.is_available && (
            <span style={styles.unavailBadge}>Currently Unavailable</span>
          )}
        </div>
        <div style={styles.itemRight}>
          {item.photo && (
            <img src={item.photo} alt={item.name} style={styles.itemPhoto} />
          )}
          {item.is_available && (
            qty === 0 ? (
              <button style={styles.addBtn} onClick={() => onAdd(item)}>
                <Plus size={16} />
              </button>
            ) : (
              <div style={styles.qtyControl}>
                <button style={styles.qtyBtn} onClick={() => onRemove(item.id)}><Minus size={12} /></button>
                <span style={styles.qtyNum}>{qty}</span>
                <button style={styles.qtyBtn} onClick={() => onAdd(item)}><Plus size={12} /></button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#FAFAFA', maxWidth: 600, margin: '0 auto' },
  loadingLogo: { fontSize: 48, marginBottom: 8 },
  header: { background: 'white', padding: '16px', borderBottom: '1px solid #ECF0F1', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  headerInner: { display: 'flex', alignItems: 'center', gap: 12 },
  logoBox: { width: 52, height: 52, background: '#fff0eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  logoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  headerInfo: { flex: 1, minWidth: 0 },
  restaurantName: { fontSize: '17px', fontWeight: '700', color: '#2C3E50', marginBottom: 2 },
  restaurantAddr: { fontSize: '11px', color: '#7F8C8D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  searchWrapper: { position: 'relative', padding: '12px 16px', background: 'white', borderBottom: '1px solid #ECF0F1' },
  searchIcon: { position: 'absolute', left: 28, top: '50%', transform: 'translateY(-50%)' },
  searchInput: { width: '100%', padding: '10px 36px 10px 36px', border: '1.5px solid #ECF0F1', borderRadius: 100, fontSize: 14, background: '#FAFAFA', color: '#2C3E50', fontFamily: 'Poppins,sans-serif', outline: 'none' },
  searchClear: { position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#7F8C8D', display: 'flex', alignItems: 'center' },
  catTabs: { display: 'flex', gap: 8, overflowX: 'auto', padding: '12px 16px', background: 'white', borderBottom: '1px solid #ECF0F1', position: 'sticky', top: '85px', zIndex: 40 },
  catTab: { padding: '7px 18px', borderRadius: 100, fontSize: 13, fontWeight: 500, background: 'white', border: '1.5px solid #ECF0F1', color: '#7F8C8D', whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 },
  catTabActive: { background: '#FF6B35', color: 'white', border: '1.5px solid #FF6B35' },
  menuContent: { padding: '16px' },
  searchResultLabel: { fontSize: '13px', color: '#7F8C8D', marginBottom: 12 },
  categorySection: { marginBottom: 24 },
  categoryTitle: { fontSize: '17px', fontWeight: '700', color: '#2C3E50', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid #FF6B35', display: 'inline-block' },
  itemCard: { background: 'white', borderRadius: 12, marginBottom: 10, padding: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'opacity 0.2s' },
  itemContent: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemVegRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 },
  itemName: { fontSize: '14px', fontWeight: '600', color: '#2C3E50' },
  itemDesc: { fontSize: '12px', color: '#7F8C8D', marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  itemPrice: { fontSize: '15px', fontWeight: '700', color: '#2C3E50' },
  unavailBadge: { display: 'inline-block', fontSize: '11px', color: '#E74C3C', background: '#fdecea', padding: '2px 8px', borderRadius: 100, marginTop: 4 },
  itemRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 },
  itemPhoto: { width: 80, height: 80, borderRadius: 10, objectFit: 'cover' },
  addBtn: { background: '#FF6B35', color: 'white', border: 'none', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  qtyControl: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff0eb', borderRadius: 8, padding: '4px 8px' },
  qtyBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#FF6B35', display: 'flex', alignItems: 'center', padding: 2 },
  qtyNum: { fontSize: '14px', fontWeight: '700', color: '#FF6B35', minWidth: 16, textAlign: 'center' },
  cartBtn: {
    position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
    background: '#FF6B35', color: 'white', border: 'none', borderRadius: 100,
    padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: 'calc(100% - 32px)', maxWidth: 568, cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(255,107,53,0.4)', fontFamily: 'Poppins,sans-serif',
  },
  cartBtnLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cartBtnCount: { background: 'rgba(255,255,255,0.25)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  cartBtnText: { fontSize: '15px', fontWeight: '600' },
  cartBtnRight: { display: 'flex', alignItems: 'center', gap: 4, fontSize: '15px', fontWeight: '600' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  cartSheet: { background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: '20px 20px 32px' },
  cartHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  cartTitle: { fontSize: '18px', fontWeight: '700', color: '#2C3E50' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#7F8C8D' },
  cartItems: { marginBottom: 12 },
  cartItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #ECF0F1' },
  cartItemInfo: { display: 'flex', alignItems: 'center', gap: 6, flex: 1 },
  cartItemName: { fontSize: '14px', fontWeight: '500', color: '#2C3E50' },
  cartItemRight: { display: 'flex', alignItems: 'center', gap: 12 },
  cartItemPrice: { fontSize: '14px', fontWeight: '700', color: '#2C3E50', minWidth: 48, textAlign: 'right' },
  subtotalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '2px solid #ECF0F1', marginBottom: 12 },
  subtotalLabel: { fontSize: '15px', fontWeight: '600', color: '#2C3E50' },
  subtotalAmount: { fontSize: '18px', fontWeight: '700', color: '#2C3E50' },
  taxNote: { display: 'flex', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 16, alignItems: 'flex-start' },
  taxNoteText: { fontSize: '12px', color: '#92400e', lineHeight: 1.5 },
  orderDetails: { marginBottom: 16 },
  waHint: { textAlign: 'center', fontSize: '12px', color: '#7F8C8D', marginTop: 8 },
};
